/**
 * SOVRANOVA — ANCHOR (Health Pillar) · Nervous-System Steadiness & Pacing
 *
 * Wellness-first self-management for energy pacing, grounding, and gentle
 * episode self-tracking. Makes NO medical or diagnostic claims and never
 * promises to prevent a symptom or flare — it helps a person keep energy in
 * reserve and stay steady.
 *
 * SAFETY (non-negotiable): logging a flare/episode ALWAYS runs a brief crisis
 * screen first. If the person signals risk, we surface real human help
 * (Lifeline 13 11 14 / 000) and record crisisEscalated — this surface can
 * never suppress escalation. The backend only persists what the screen decided.
 *
 * Backend: /api/anchor (baseline, pacing/evaluate, pacing/mini-rest, episode,
 * grounding, summary). Requires the anchor_* Supabase tables.
 */
import { useState, useEffect, useCallback } from 'react'
import { api } from '../../services/api'
import { getCrisisResources, resolveRegion, HELPLINE_DIRECTORY_URL } from '../../services/crisisResources'

const TABS = [
  { id: 'pace',     emoji: '🔋', label: 'Pace' },
  { id: 'ground',   emoji: '🌿', label: 'Ground' },
  { id: 'flare',    emoji: '🌊', label: 'Steady a flare' },
  { id: 'patterns', emoji: '📊', label: 'Patterns' },
  { id: 'baseline', emoji: '⚙️', label: 'Baseline' },
]

const ACTIVITY_TYPES = [
  { id: 'physical',  label: 'Physical' },
  { id: 'mental',    label: 'Mental' },
  { id: 'emotional', label: 'Emotional' },
  { id: 'sensory',   label: 'Sensory' },
]

const GROUNDING = [
  { id: 'external_anchor',   emoji: '🧭', name: 'External Anchor',
    steps: 'Find one fixed point — a doorframe, the horizon, a mark on the wall. Let your eyes rest there and name it to yourself. It is steady. You are here, and you are steady too.' },
  { id: 'sensory_5_4_3_2_1', emoji: '🖐️', name: '5-4-3-2-1',
    steps: 'Name 5 things you can see, 4 you can feel, 3 you can hear, 2 you can smell, 1 you can taste. Slow and unhurried — let each one bring you a little more here.' },
  { id: 'extended_exhale',   emoji: '🌬️', name: 'Extended Exhale',
    steps: 'Breathe in gently for a count of 4, then out for 6 to 8. The long exhale tells your nervous system it is safe to settle. Repeat six slow rounds.' },
  { id: 'pain_reset',        emoji: '🪶', name: 'Pain Reset',
    steps: 'Soften around the place that hurts rather than bracing against it. A slow breath toward it, a longer breath out. You are not fighting it — just making a little room.' },
]

const EPISODE_TYPES = [
  { id: 'early_warning',  label: 'Early warning' },
  { id: 'physical_flare', label: 'Physical flare' },
  { id: 'overwhelm',      label: 'Overwhelm' },
  { id: 'pain_spike',     label: 'Pain spike' },
  { id: 'low_energy',     label: 'Low energy' },
]

const EXPERIENCES = [
  { id: 'tremor',          label: 'Tremor' },
  { id: 'weakness',        label: 'Weakness' },
  { id: 'disconnection',   label: 'Disconnection' },
  { id: 'fatigue',         label: 'Fatigue' },
  { id: 'sensory_overload',label: 'Sensory overload' },
  { id: 'pain',            label: 'Pain' },
]

const STIMULI = ['Heat', 'Noise', 'Crowds', 'Stress', 'Poor sleep', 'Screen time', 'Skipped meal', 'Overexertion']

function zoneColor(zone) {
  return zone === 'red' ? '#e2725b' : zone === 'yellow' ? '#e6a020' : '#3fb98a'
}
function batteryZone(b) {
  return b <= 20 ? 'red' : b <= 40 ? 'yellow' : 'green'
}

export function AnchorView({ onClose, onHome }) {
  const [tab, setTab] = useState('pace')
  const [baseline, setBaseline] = useState(null)

  useEffect(() => {
    api.get('/anchor/baseline').then(r => setBaseline(r?.baseline || null)).catch(() => {})
  }, [])

  return (
    <div style={s.page}>
      <div style={s.topbar}>
        <button style={s.back} onClick={onClose}>← Back</button>
        <button style={s.home} onClick={onHome}>⌂</button>
      </div>

      <header style={s.hero}>
        <p style={s.kicker}>SOVRANOVA · ANCHOR</p>
        <h1 style={s.h1}>Steadiness &amp; pacing</h1>
        <p style={s.sub}>A calm companion for keeping energy in reserve and settling your nervous system. This is self-management and support — not medical advice, and never a promise to prevent a symptom.</p>
      </header>

      <nav style={s.tabs}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ ...s.tab, ...(tab === t.id ? s.tabOn : {}) }}>
            <span style={s.tabEmoji}>{t.emoji}</span>
            <span>{t.label}</span>
          </button>
        ))}
      </nav>

      <div style={s.panel}>
        {tab === 'pace'     && <PacePanel baseline={baseline} />}
        {tab === 'ground'   && <GroundPanel />}
        {tab === 'flare'    && <FlarePanel />}
        {tab === 'patterns' && <PatternsPanel />}
        {tab === 'baseline' && <BaselinePanel baseline={baseline} onSaved={setBaseline} />}
      </div>

      <p style={s.foot}>ANCHOR supports the steadying side of living with a changeable body. If something feels medically urgent, contact your clinician or call {(() => { const r = getCrisisResources(resolveRegion()); return r.emergency.tel ? r.emergency.number : 'your local emergency number' })()}. You're not alone with this.</p>
    </div>
  )
}

// ── PACE: battery + activity → 75% rule recommendation ──────────────────────
function PacePanel({ baseline }) {
  const [battery, setBattery] = useState(70)
  const [label, setLabel] = useState('')
  const [type, setType] = useState('physical')
  const [minutes, setMinutes] = useState(30)
  const [rec, setRec] = useState(null)
  const [busy, setBusy] = useState(false)
  const [restLogged, setRestLogged] = useState(false)
  const [err, setErr] = useState(null)

  const evaluate = async () => {
    setBusy(true); setErr(null); setRestLogged(false)
    try {
      const r = await api.post('/anchor/pacing/evaluate', {
        battery,
        activity: { label: label.trim() || null, type, plannedMinutes: Number(minutes) || 0 },
      })
      setRec(r?.recommendation || null)
    } catch (e) {
      setErr('Could not pace that just now. Your guidance still holds: keep some in reserve.')
    } finally { setBusy(false) }
  }

  const logMiniRest = async () => {
    try { await api.post('/anchor/pacing/mini-rest'); setRestLogged(true) } catch { setRestLogged(true) }
  }

  const z = batteryZone(battery)

  return (
    <div style={s.col}>
      <h3 style={s.panelTitle}>Where's your battery right now?</h3>
      <p style={s.help}>A self-awareness tool, not a clinical measure. Be honest with yourself — low is information, not failure.</p>

      <div style={s.batteryRow}>
        <input type="range" min="0" max="100" value={battery}
          onChange={e => setBattery(Number(e.target.value))} style={{ ...s.range, accentColor: zoneColor(z) }} />
        <span style={{ ...s.batteryVal, color: zoneColor(z) }}>{battery}%</span>
      </div>
      <p style={{ ...s.zoneNote, color: zoneColor(z) }}>
        {z === 'red' ? 'Rest window — defer demand.' : z === 'yellow' ? 'Yellow zone — protect your reserves.' : 'Green — steady. You can pace something.'}
      </p>

      <div style={s.divider} />

      <h3 style={s.panelTitle}>Pace one activity</h3>
      <input style={s.input} placeholder="What is it? (e.g. cook dinner, a call, errands)" value={label} onChange={e => setLabel(e.target.value)} />
      <div style={s.chips}>
        {ACTIVITY_TYPES.map(t => (
          <button key={t.id} onClick={() => setType(t.id)} style={{ ...s.chip, ...(type === t.id ? s.chipOn : {}) }}>{t.label}</button>
        ))}
      </div>
      <label style={s.fieldRow}>
        <span style={s.fieldLabel}>Planned minutes</span>
        <input type="number" min="0" max="600" value={minutes} onChange={e => setMinutes(e.target.value)} style={s.numInput} />
      </label>

      <button style={s.primaryBtn} onClick={evaluate} disabled={busy}>{busy ? 'Pacing…' : 'Pace it'}</button>
      {err && <p style={s.err}>{err}</p>}

      {rec && (
        <div style={{ ...s.recCard, borderColor: zoneColor(rec.zone) }}>
          <p style={{ ...s.recZone, color: zoneColor(rec.zone) }}>
            {rec.zone === 'green' ? '✓ Good to go' : rec.zone === 'yellow' ? '◐ Hold for now' : '✕ Rest window'}
          </p>
          <p style={s.recMsg}>{rec.message}</p>
          {rec.safeToProceed && rec.targetMinutes > 0 && (
            <p style={s.recTarget}>Aim to ease off around <strong>{rec.targetMinutes} min</strong>.</p>
          )}
          {(!rec.safeToProceed || rec.miniRestMinutes > 0) && (
            restLogged
              ? <p style={s.restOk}>🌿 Mini-rest logged. That's a real win.</p>
              : <button style={s.softBtn} onClick={logMiniRest}>I took a {rec.miniRestMinutes || 10}-min mini-rest</button>
          )}
        </div>
      )}
    </div>
  )
}

// ── GROUND: pick a protocol, optional distress before/after ─────────────────
function GroundPanel() {
  const [active, setActive] = useState(null)
  const [before, setBefore] = useState(null)
  const [after, setAfter] = useState(null)
  const [saved, setSaved] = useState(false)
  const proto = GROUNDING.find(g => g.id === active)

  const finish = async () => {
    try {
      await api.post('/anchor/grounding', {
        protocol: active, completed: true,
        distressBefore: before, distressAfter: after,
      })
    } catch { /* non-blocking */ }
    setSaved(true)
  }

  if (proto) {
    return (
      <div style={s.col}>
        <button style={s.backLink} onClick={() => { setActive(null); setBefore(null); setAfter(null); setSaved(false) }}>← All exercises</button>
        <h3 style={s.panelTitle}><span style={s.gEmoji}>{proto.emoji}</span> {proto.name}</h3>
        <p style={s.protoSteps}>{proto.steps}</p>

        {!saved ? (
          <>
            <Distress label="Before we start, how settled do you feel? (0 calm → 10 distressed)" value={before} onChange={setBefore} />
            <Distress label="After the exercise — and only when you're ready" value={after} onChange={setAfter} />
            <button style={s.primaryBtn} onClick={finish}>Done — I grounded</button>
            <p style={s.help}>The before/after is optional, just for your own insight.</p>
          </>
        ) : (
          <div style={s.recCard}>
            <p style={s.restOk}>🌿 Logged. You took a moment to steady yourself.</p>
            {before != null && after != null && (
              <p style={s.recMsg}>{after < before ? 'And it eased things a little — that matters.' : 'Some days it holds steady rather than dropping. Still worth doing.'}</p>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div style={s.col}>
      <h3 style={s.panelTitle}>Settle your system</h3>
      <p style={s.help}>Short down-regulation exercises. Pick whatever feels reachable right now.</p>
      <div style={s.grid}>
        {GROUNDING.map(g => (
          <button key={g.id} style={s.gCard} onClick={() => setActive(g.id)}>
            <span style={s.gCardEmoji}>{g.emoji}</span>
            <span style={s.gCardName}>{g.name}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

function Distress({ label, value, onChange }) {
  return (
    <div style={s.distressBlock}>
      <span style={s.fieldLabel}>{label}</span>
      <div style={s.distressRow}>
        {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
          <button key={n} onClick={() => onChange(n)} style={{ ...s.dDot, ...(value === n ? s.dDotOn : {}) }}>{n}</button>
        ))}
      </div>
    </div>
  )
}

// ── FLARE: crisis screen FIRST, then low-friction episode log ───────────────
function FlarePanel() {
  // stage: 'check' (crisis screen) → 'safe' (log form) | 'escalate' (lifeline) → 'done'
  const [stage, setStage] = useState('check')
  const [escalated, setEscalated] = useState(false)
  const [episodeType, setEpisodeType] = useState('early_warning')
  const [experience, setExperience] = useState(null)
  const [stimuli, setStimuli] = useState([])
  const [battery, setBattery] = useState(40)
  const [busy, setBusy] = useState(false)

  const toggleStim = (label) =>
    setStimuli(prev => prev.includes(label) ? prev.filter(x => x !== label) : [...prev, label])

  const logEpisode = async () => {
    setBusy(true)
    try {
      await api.post('/anchor/episode', {
        episodeType,
        primaryExperience: experience,
        contextStimuli: stimuli,
        batteryAtOnset: battery,
        groundingCompleted: false,
        crisisScreened: true,
        crisisEscalated: escalated,
      })
    } catch { /* non-blocking — never block on a logging failure */ }
    setBusy(false); setStage('done')
  }

  if (stage === 'check') {
    return (
      <div style={s.col}>
        <h3 style={s.panelTitle}>Before we note this down</h3>
        <p style={s.screenLead}>First, gently — right now, are you having any thoughts of harming yourself or of not wanting to be here?</p>
        <button style={s.safeBtn} onClick={() => setStage('safe')}>No — I'm safe, just having a hard moment</button>
        <button style={s.riskBtn} onClick={() => { setEscalated(true); setStage('escalate') }}>Yes, or I'm not sure</button>
        <p style={s.help}>There's no wrong answer here. This always comes first.</p>
      </div>
    )
  }

  if (stage === 'escalate') {
    return (
      <div style={s.col}>
        <h3 style={s.panelTitle}>You matter, and you don't have to hold this alone</h3>
        <p style={s.screenLead}>Please reach out right now — someone is there to talk it through with you, any hour.</p>
        {(() => {
          const res = getCrisisResources(resolveRegion())
          return (
            <>
              {res.primary.tel
                ? <a href={`tel:${res.primary.tel}`} style={s.callPrimary}><span>{res.primary.label}</span><span style={s.callNum}>{res.primary.number}</span></a>
                : <a href={HELPLINE_DIRECTORY_URL} target="_blank" rel="noopener noreferrer" style={s.callPrimary}><span>{res.primary.label}</span><span style={s.callNum}>findahelpline.com</span></a>}
              {res.emergency.tel
                ? <a href={`tel:${res.emergency.tel}`} style={s.callSecondary}><span>Emergency</span><span style={s.callNum}>{res.emergency.number}</span></a>
                : <div style={s.callSecondary}><span>Emergency</span><span style={s.callNum}>your local number</span></div>}
              <p style={s.region}>{res.region} · 24/7</p>
              <p style={{ ...s.help, marginTop: '0.35rem' }}>
                Not in {res.region}?{' '}
                <a href={HELPLINE_DIRECTORY_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Find support in your country</a>
                {' '}· or go to your nearest emergency department.
              </p>
            </>
          )
        })()}
        <div style={s.divider} />
        <p style={s.help}>When you're ready — and only then — you can still note this episode for your own awareness. It will be recorded that support was offered.</p>
        <button style={s.softBtn} onClick={() => setStage('safe')}>Note the episode</button>
      </div>
    )
  }

  if (stage === 'done') {
    return (
      <div style={s.col}>
        <div style={s.recCard}>
          <p style={s.restOk}>⚓ Noted. Naming a flare is how you start to see your patterns.</p>
          {escalated && <p style={s.recMsg}>And please — keep that lifeline close. {(() => { const r = getCrisisResources(resolveRegion()); return r.primary.tel ? `${r.primary.label} ${r.primary.number} is` : 'The Urgent help button in this app is' })()} there whenever you need it.</p>}
        </div>
        <button style={s.softBtn} onClick={() => { setStage('check'); setEscalated(false); setExperience(null); setStimuli([]) }}>Log another</button>
      </div>
    )
  }

  // stage === 'safe' → the actual low-friction log
  return (
    <div style={s.col}>
      <h3 style={s.panelTitle}>What's happening?</h3>
      <p style={s.help}>One-tap notes, just for you. No cause, no judgement — only your own pattern awareness.</p>

      <span style={s.fieldLabel}>Type</span>
      <div style={s.chips}>
        {EPISODE_TYPES.map(t => (
          <button key={t.id} onClick={() => setEpisodeType(t.id)} style={{ ...s.chip, ...(episodeType === t.id ? s.chipOn : {}) }}>{t.label}</button>
        ))}
      </div>

      <span style={s.fieldLabel}>Mainly, it feels like</span>
      <div style={s.chips}>
        {EXPERIENCES.map(t => (
          <button key={t.id} onClick={() => setExperience(experience === t.id ? null : t.id)} style={{ ...s.chip, ...(experience === t.id ? s.chipOn : {}) }}>{t.label}</button>
        ))}
      </div>

      <span style={s.fieldLabel}>Anything around you right now? (optional)</span>
      <div style={s.chips}>
        {STIMULI.map(t => (
          <button key={t} onClick={() => toggleStim(t)} style={{ ...s.chip, ...(stimuli.includes(t) ? s.chipOn : {}) }}>{t}</button>
        ))}
      </div>

      <label style={s.fieldRow}>
        <span style={s.fieldLabel}>Battery at onset</span>
        <input type="number" min="0" max="100" value={battery} onChange={e => setBattery(Number(e.target.value))} style={s.numInput} />
      </label>

      <button style={s.primaryBtn} onClick={logEpisode} disabled={busy}>{busy ? 'Saving…' : 'Note it'}</button>
    </div>
  )
}

// ── PATTERNS: steadying behaviours, never severity ──────────────────────────
function PatternsPanel() {
  const [summary, setSummary] = useState(undefined)
  useEffect(() => {
    api.get('/anchor/summary').then(r => setSummary(r?.summary || null)).catch(() => setSummary(null))
  }, [])

  if (summary === undefined) return <p style={s.help}>Loading your patterns…</p>
  if (!summary) return <p style={s.help}>No patterns yet. As you pace and ground, your steadying wins show up here.</p>

  const stats = [
    { label: 'Mini-rests taken', value: summary.miniRestsTaken, hint: 'Each one protects your reserves.' },
    { label: 'Pacing check-ins', value: summary.pacingCheckIns, hint: 'Noticing before pushing.' },
    { label: 'Grounding completed', value: summary.groundingCompleted, hint: 'Times you settled your system.' },
    { label: 'Grounding that eased things', value: summary.groundingEasedDistress, hint: 'When it brought distress down.' },
  ]

  return (
    <div style={s.col}>
      <h3 style={s.panelTitle}>Your steadying patterns</h3>
      <p style={s.help}>This shows what's <em>working</em> — the steadying things you do — not symptom severity.</p>
      <div style={s.statGrid}>
        {stats.map(st => (
          <div key={st.label} style={s.statCard}>
            <span style={s.statVal}>{st.value ?? 0}</span>
            <span style={s.statLabel}>{st.label}</span>
            <span style={s.statHint}>{st.hint}</span>
          </div>
        ))}
      </div>
      <p style={s.noteLine}>{summary.note || 'Pattern view shows steadying behaviours, not symptom severity.'}</p>
    </div>
  )
}

// ── BASELINE: per-user pacing baselines + 75% rule ──────────────────────────
function BaselinePanel({ baseline, onSaved }) {
  const [physical, setPhysical] = useState(baseline?.physical_baseline_minutes ?? 20)
  const [mental, setMental] = useState(baseline?.mental_baseline_minutes ?? 30)
  const [emotional, setEmotional] = useState(baseline?.emotional_baseline_minutes ?? 30)
  const [pct, setPct] = useState(baseline?.pacing_rule_pct ?? 75)
  const [clinician, setClinician] = useState(baseline?.has_clinician_plan ?? false)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)
  const [err, setErr] = useState(null)

  const save = useCallback(async () => {
    setBusy(true); setErr(null); setSaved(false)
    try {
      const r = await api.put('/anchor/baseline', {
        physicalBaselineMinutes: Number(physical),
        mentalBaselineMinutes: Number(mental),
        emotionalBaselineMinutes: Number(emotional),
        pacingRulePct: Number(pct),
        hasClinicianPlan: clinician,
      })
      if (r?.baseline) onSaved(r.baseline)
      setSaved(true)
    } catch (e) {
      setErr('Could not save just now. Your settings are kept here — try again in a moment.')
    } finally { setBusy(false) }
  }, [physical, mental, emotional, pct, clinician, onSaved])

  return (
    <div style={s.col}>
      <h3 style={s.panelTitle}>Your pacing baseline</h3>
      <p style={s.help}>How long you can usually do each kind of activity on a steady day. The pacing rule keeps you working at a fraction of that, so there's always something left in the tank.</p>

      <NumField label="Physical baseline (min)" value={physical} onChange={setPhysical} />
      <NumField label="Mental baseline (min)"   value={mental}   onChange={setMental} />
      <NumField label="Emotional baseline (min)" value={emotional} onChange={setEmotional} />

      <label style={s.fieldRow}>
        <span style={s.fieldLabel}>Pacing rule: work to {pct}%</span>
      </label>
      <input type="range" min="25" max="100" step="5" value={pct} onChange={e => setPct(Number(e.target.value))} style={{ ...s.range, accentColor: '#3fb98a' }} />
      <p style={s.help}>The classic starting point is 75% — ease off before you're spent, not after.</p>

      <label style={s.toggleRow} onClick={() => setClinician(c => !c)}>
        <span style={{ ...s.toggle, ...(clinician ? s.toggleOn : {}) }}>{clinician ? '✓' : ''}</span>
        <span style={s.fieldLabel}>I have a pacing or recovery plan from a clinician</span>
      </label>

      <button style={s.primaryBtn} onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save baseline'}</button>
      {saved && <p style={s.restOk}>✓ Saved. Your pacing now uses these.</p>}
      {err && <p style={s.err}>{err}</p>}
    </div>
  )
}

function NumField({ label, value, onChange }) {
  return (
    <label style={s.fieldRow}>
      <span style={s.fieldLabel}>{label}</span>
      <input type="number" min="0" max="600" value={value} onChange={e => onChange(e.target.value)} style={s.numInput} />
    </label>
  )
}

// ── Styles — calm slate/teal palette ────────────────────────────────────────
const s = {
  page:    { minHeight:'100vh', background:'linear-gradient(170deg,#0c1418,#0a1a1f,#0a1518)', padding:'1.1rem 1.1rem 2.5rem', display:'flex', flexDirection:'column', gap:'1.1rem', color:'#e6f0f0' },
  topbar:  { display:'flex', alignItems:'center', justifyContent:'space-between' },
  back:    { background:'transparent', border:'none', color:'rgba(230,240,240,.6)', fontSize:'0.9rem', cursor:'pointer' },
  home:    { background:'transparent', border:'none', color:'rgba(230,240,240,.6)', fontSize:'1.2rem', cursor:'pointer' },
  hero:    { display:'flex', flexDirection:'column', gap:'0.35rem' },
  kicker:  { fontSize:'0.64rem', letterSpacing:'0.16em', textTransform:'uppercase', color:'#4fd1b0', fontWeight:700, margin:0 },
  h1:      { fontSize:'1.85rem', fontWeight:800, color:'#eef6f4', lineHeight:1.1, letterSpacing:'-0.01em', margin:0 },
  sub:     { fontSize:'0.9rem', color:'rgba(230,240,240,.6)', lineHeight:1.55, margin:0 },
  tabs:    { display:'flex', gap:'0.4rem', overflowX:'auto', paddingBottom:'0.2rem' },
  tab:     { display:'flex', alignItems:'center', gap:'0.35rem', flexShrink:0, background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.08)', color:'rgba(230,240,240,.7)', borderRadius:'999px', padding:'0.5rem 0.85rem', fontSize:'0.8rem', fontWeight:600, cursor:'pointer', fontFamily:'inherit' },
  tabOn:   { background:'rgba(79,209,176,.16)', border:'1px solid rgba(79,209,176,.5)', color:'#bff0e4' },
  tabEmoji:{ fontSize:'0.95rem' },
  panel:   { background:'rgba(255,255,255,.03)', border:'1px solid rgba(255,255,255,.07)', borderRadius:'1.1rem', padding:'1.1rem', minHeight:200 },
  col:     { display:'flex', flexDirection:'column', gap:'0.7rem' },
  panelTitle:{ fontSize:'1.05rem', fontWeight:700, color:'#eef6f4', margin:0 },
  help:    { fontSize:'0.78rem', color:'rgba(230,240,240,.5)', lineHeight:1.5, margin:0 },
  divider: { height:1, background:'rgba(255,255,255,.08)', margin:'0.4rem 0' },
  batteryRow:{ display:'flex', alignItems:'center', gap:'0.8rem' },
  range:   { flex:1, height:6, cursor:'pointer' },
  batteryVal:{ fontSize:'1.3rem', fontWeight:800, minWidth:'3.2rem', textAlign:'right' },
  zoneNote:{ fontSize:'0.82rem', fontWeight:600, margin:0 },
  input:   { width:'100%', boxSizing:'border-box', background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.12)', borderRadius:'0.7rem', padding:'0.7rem 0.8rem', color:'#eef6f4', fontSize:'0.9rem', fontFamily:'inherit' },
  chips:   { display:'flex', flexWrap:'wrap', gap:'0.4rem' },
  chip:    { background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.12)', color:'rgba(230,240,240,.7)', borderRadius:'999px', padding:'0.4rem 0.75rem', fontSize:'0.78rem', cursor:'pointer', fontFamily:'inherit' },
  chipOn:  { background:'rgba(79,209,176,.18)', border:'1px solid rgba(79,209,176,.55)', color:'#bff0e4', fontWeight:600 },
  fieldRow:{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.8rem' },
  fieldLabel:{ fontSize:'0.82rem', color:'rgba(230,240,240,.75)', fontWeight:500 },
  numInput:{ width:'5rem', background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.12)', borderRadius:'0.6rem', padding:'0.5rem 0.6rem', color:'#eef6f4', fontSize:'0.9rem', fontFamily:'inherit', textAlign:'center' },
  primaryBtn:{ marginTop:'0.3rem', padding:'0.85rem 1rem', width:'100%', background:'linear-gradient(135deg,#1e7a6a,#3fb98a)', color:'#fff', border:'none', borderRadius:'0.85rem', fontSize:'0.95rem', fontWeight:700, cursor:'pointer', fontFamily:'inherit', boxShadow:'0 4px 18px rgba(63,185,138,.28)' },
  softBtn: { padding:'0.7rem 1rem', width:'100%', background:'rgba(255,255,255,.07)', color:'#dff3ec', border:'1px solid rgba(255,255,255,.16)', borderRadius:'0.8rem', fontSize:'0.88rem', fontWeight:600, cursor:'pointer', fontFamily:'inherit' },
  err:     { fontSize:'0.78rem', color:'#e89', lineHeight:1.5, margin:0 },
  recCard: { background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.12)', borderRadius:'0.9rem', padding:'0.9rem', display:'flex', flexDirection:'column', gap:'0.45rem' },
  recZone: { fontSize:'0.95rem', fontWeight:800, margin:0 },
  recMsg:  { fontSize:'0.88rem', color:'#e6f0f0', lineHeight:1.55, margin:0 },
  recTarget:{ fontSize:'0.85rem', color:'rgba(230,240,240,.8)', margin:0 },
  restOk:  { fontSize:'0.88rem', color:'#7fe0c4', fontWeight:600, margin:0 },
  grid:    { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.6rem' },
  gCard:   { display:'flex', flexDirection:'column', alignItems:'center', gap:'0.4rem', background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.1)', borderRadius:'0.9rem', padding:'1.1rem 0.6rem', cursor:'pointer', color:'#eef6f4' },
  gCardEmoji:{ fontSize:'1.7rem' },
  gCardName:{ fontSize:'0.85rem', fontWeight:600 },
  gEmoji:  { fontSize:'1.2rem', marginRight:'0.3rem' },
  backLink:{ alignSelf:'flex-start', background:'transparent', border:'none', color:'rgba(230,240,240,.55)', fontSize:'0.82rem', cursor:'pointer', padding:0 },
  protoSteps:{ fontSize:'0.95rem', color:'#e6f0f0', lineHeight:1.65, margin:0 },
  distressBlock:{ display:'flex', flexDirection:'column', gap:'0.4rem' },
  distressRow:{ display:'flex', flexWrap:'wrap', gap:'0.3rem' },
  dDot:    { width:'2rem', height:'2rem', borderRadius:'0.5rem', background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.12)', color:'rgba(230,240,240,.65)', fontSize:'0.78rem', cursor:'pointer', fontFamily:'inherit' },
  dDotOn:  { background:'rgba(79,209,176,.22)', border:'1px solid rgba(79,209,176,.6)', color:'#bff0e4', fontWeight:700 },
  screenLead:{ fontSize:'0.95rem', color:'#eef6f4', lineHeight:1.6, margin:0 },
  safeBtn: { padding:'0.85rem 1rem', width:'100%', background:'rgba(79,209,176,.14)', color:'#cdf3e8', border:'1px solid rgba(79,209,176,.45)', borderRadius:'0.85rem', fontSize:'0.9rem', fontWeight:600, cursor:'pointer', fontFamily:'inherit' },
  riskBtn: { padding:'0.85rem 1rem', width:'100%', background:'rgba(226,114,91,.12)', color:'#f1c3b6', border:'1px solid rgba(226,114,91,.5)', borderRadius:'0.85rem', fontSize:'0.9rem', fontWeight:600, cursor:'pointer', fontFamily:'inherit' },
  callPrimary:{ display:'flex', justifyContent:'space-between', alignItems:'center', textDecoration:'none', padding:'0.7rem 0.85rem', borderRadius:'0.75rem', background:'rgba(201,162,39,.18)', border:'1px solid rgba(201,162,39,.55)', color:'#f7eccb', fontWeight:700 },
  callSecondary:{ display:'flex', justifyContent:'space-between', alignItems:'center', textDecoration:'none', padding:'0.7rem 0.85rem', borderRadius:'0.75rem', background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.18)', color:'#f3ede1', fontWeight:600 },
  callNum: { fontSize:'1rem', fontWeight:800, letterSpacing:'0.02em' },
  region:  { fontSize:'0.7rem', color:'rgba(230,240,240,.5)', textAlign:'center', margin:0 },
  statGrid:{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.6rem' },
  statCard:{ display:'flex', flexDirection:'column', gap:'0.2rem', background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.1)', borderRadius:'0.9rem', padding:'0.9rem' },
  statVal: { fontSize:'1.7rem', fontWeight:800, color:'#4fd1b0' },
  statLabel:{ fontSize:'0.8rem', fontWeight:600, color:'#eef6f4' },
  statHint:{ fontSize:'0.7rem', color:'rgba(230,240,240,.5)', lineHeight:1.4 },
  noteLine:{ fontSize:'0.76rem', color:'rgba(230,240,240,.45)', fontStyle:'italic', lineHeight:1.5, margin:0 },
  toggleRow:{ display:'flex', alignItems:'center', gap:'0.6rem', cursor:'pointer', padding:'0.3rem 0' },
  toggle:  { width:'1.4rem', height:'1.4rem', borderRadius:'0.4rem', border:'1px solid rgba(255,255,255,.25)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.85rem', color:'#3fb98a', flexShrink:0 },
  toggleOn:{ background:'rgba(79,209,176,.2)', border:'1px solid rgba(79,209,176,.6)' },
  foot:    { fontSize:'0.74rem', color:'rgba(230,240,240,.4)', lineHeight:1.6, fontStyle:'italic', borderTop:'1px solid rgba(255,255,255,.08)', paddingTop:'0.9rem', margin:0 },
}
